import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    calendarOutline,
    chevronForwardOutline,
    americanFootballOutline,
    locationOutline,
    peopleOutline,
    statsChartOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    GameDetail,
    GamePlay,
    Team,
    getGameDetail,
    getTeams,
    PlayersById,
    getPlayers
} from "../services/mexfbaApi";
import GamePrediction from "../components/GamePrediction";
import GameStandings from "../components/GameStandings";
import GameHighlights from "../components/GameHighlights";

import "./Game.css";

interface RouteParams {
    id: string;
}

interface ScoreEvent {
    id: string;
    quarter: string;
    teamId: string;
    teamName: string;
    points: number;
    description: string;
}

interface ScoreBoxProps {
    game: GameDetail["Data"];
    plays: GamePlay[];
    status: string;
}

interface PeriodScores {
    local: Record<string, number>;
    visitor: Record<string, number>;
    hasOvertime: boolean;
    hasUnassignedScore: boolean;
}

function calculatePeriodScores(
    game: GameDetail["Data"],
    plays: GamePlay[]
): PeriodScores {
    const result: PeriodScores = {
        local: {
            "1": 0,
            "2": 0,
            "3": 0,
            "4": 0,
            "OT": 0
        },
        visitor: {
            "1": 0,
            "2": 0,
            "3": 0,
            "4": 0,
            "OT": 0
        },
        hasOvertime: false,
        hasUnassignedScore: false
    };

    plays.forEach(function (play) {
        const rawQuarter = String(play.q || "")
            .trim()
            .toUpperCase();

        /*
            Acepta "OT" y formatos como "OT1" o "2OT".
            Si hay varios tiempos extra, se suman en la columna OT.
        */
        const isOvertime =
            rawQuarter === "OT" ||
            /^OT\d+$/.test(rawQuarter) ||
            /^\d+OT$/.test(rawQuarter);

        if (isOvertime) {
            result.hasOvertime = true;
        }

        const points = getPlayPoints(play);

        if (points <= 0) {
            return;
        }

        const quarter = isOvertime
            ? "OT"
            : rawQuarter;

        if (!["1", "2", "3", "4", "OT"].includes(quarter)) {
            result.hasUnassignedScore = true;
            return;
        }

        /*
            Evitar asignar una anotación al equipo equivocado
            si no se reconoce la ofensiva.
        */
        if (
            play.ofensiva !== game.id_local &&
            play.ofensiva !== game.id_visita
        ) {
            result.hasUnassignedScore = true;
            return;
        }

        /*
            Solo uno de estos campos debería tener puntos.
            Si hay más de uno, no adivinamos cuál corresponde.
        */
        const scoringFields = [
            play.td,
            play.deftd,
            play.retTd
        ].filter(function (value) {
            return getNumber(value) > 0;
        });

        if (scoringFields.length > 1) {
            result.hasUnassignedScore = true;
            return;
        }

        const scoringTeamId = getPlayScoringTeam(
            play,
            game.id_local,
            game.id_visita
        );

        if (scoringTeamId === game.id_local) {
            result.local[quarter] += points;
        } else if (scoringTeamId === game.id_visita) {
            result.visitor[quarter] += points;
        } else {
            result.hasUnassignedScore = true;
        }
    });

    return result;
}

const ScoreBox: React.FC<ScoreBoxProps> = ({
    game,
    plays,
    status
}) => {
    const scores = calculatePeriodScores(game, plays);

    const periods = scores.hasOvertime
        ? ["1", "2", "3", "4", "OT"]
        : ["1", "2", "3", "4"];

    const localTotal = periods.reduce(function (total, period) {
        return total + scores.local[period];
    }, 0);

    const visitorTotal = periods.reduce(function (total, period) {
        return total + scores.visitor[period];
    }, 0);

    return (
        <section className="game-section">
            <div className="game-scorebox">
                <div className="game-scorebox__scroll">
                    <table className="game-scorebox__table">
                        <caption>
                            Marcador por cuarto del partido
                        </caption>

                        <thead>
                            <tr>
                                <th scope="col">
                                    {status}
                                </th>

                                {periods.map(function (period) {
                                    return (
                                        <th scope="col" key={period}>
                                            {period}
                                        </th>
                                    );
                                })}

                                <th
                                    scope="col"
                                    className="game-scorebox__total"
                                >
                                    T
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr>
                                <th
                                    scope="row"
                                    className="game-scorebox__team"
                                >
                                    <span>
                                        {game.nombre_local || "Local"}
                                    </span>
                                </th>

                                {periods.map(function (period) {
                                    return (
                                        <td key={period}>
                                            {scores.local[period]}
                                        </td>
                                    );
                                })}

                                <td className="game-scorebox__total">
                                    {localTotal}
                                </td>
                            </tr>

                            <tr>
                                <th
                                    scope="row"
                                    className="game-scorebox__team"
                                >
                                    <span>
                                        {game.nombre_visita || "Visitante"}
                                    </span>
                                </th>

                                {periods.map(function (period) {
                                    return (
                                        <td key={period}>
                                            {scores.visitor[period]}
                                        </td>
                                    );
                                })}

                                <td className="game-scorebox__total">
                                    {visitorTotal}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {scores.hasUnassignedScore && (
                    <p className="game-scorebox__warning" role="status">
                        Algunas anotaciones no pudieron asignarse a un
                        equipo o periodo. El marcador puede estar incompleto.
                    </p>
                )}
            </div>
        </section>
    );
};

function parseDate(date?: string): Date | null {
    if (!date) {
        return null;
    }

    const normalizedDate =
        date.length === 10 ? `${date}T12:00:00` : date;

    const parsedDate = new Date(normalizedDate);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}
function getNumber(value: string | undefined): number {
    const numberValue = Number(value);

    return Number.isFinite(numberValue) ? numberValue : 0;
}
function getScoreDescription(play: GamePlay): string {
    const touchdown = getNumber(play.td);
    const defensiveTouchdown = getNumber(play.deftd);
    const returnTouchdown = getNumber(play.retTd);

    if (touchdown === 6) {
        return " Touchdown.";
    }

    if (defensiveTouchdown === 6) {
        return " Touchdown defensivo.";
    }

    if (returnTouchdown === 6) {
        return " Touchdown de regreso.";
    }

    if (touchdown === 3) {
        return " Gol de campo bueno.";
    }

    if (touchdown === 2) {
        return " Conversión de dos puntos buena.";
    }

    if (touchdown === 1) {
        return " Punto extra bueno.";
    }

    if (defensiveTouchdown === 2) {
        return " Safety.";
    }

    if (play.d === "5" && touchdown === 0) {
        return " Conversión no lograda.";
    }

    return "";
}
function hasValue(value?: string | null): boolean {
    return value !== undefined && value !== null && value !== "";
}
function getPlayerName(
    playerId: string | undefined,
    players: PlayersById
): string {
    if (!playerId) {
        return "";
    }

    return players[playerId]?.nombre || "Jugador";
}
function getAssistedTackle(
    playerId: string | undefined,
    players: PlayersById
): string {
    const playerName = getPlayerName(playerId, players);

    return playerName ? `, asistido por ${playerName}` : "";
}

function getTackleText(
    play: GamePlay,
    players: PlayersById
): string {
    const tackler = getPlayerName(play.tkl, players);

    if (!tackler) {
        return "";
    }

    return ` Tackle de ${tackler}${getAssistedTackle(
        play.assTkl,
        players
    )}${getAssistedTackle(play.assTkll, players)}.`;
}

function getFumbleText(
    play: GamePlay,
    players: PlayersById
): string {
    const forcedBy = getPlayerName(play.fumbleF, players);
    const recoveredBy = getPlayerName(play.fumbleR, players);

    if (!forcedBy && !recoveredBy) {
        return "";
    }

    let description = " Fumble.";

    if (forcedBy) {
        description += ` Forzado por ${forcedBy}.`;
    }

    if (recoveredBy) {
        description += ` Recuperado por ${recoveredBy}.`;
    }

    return description;
}
function getPlayDescription(
    play: GamePlay,
    players: PlayersById
): string {
    const quarterback = getPlayerName(play.QB, players);
    const runner = getPlayerName(play.RB, players);
    const receiver = getPlayerName(play.WR, players);
    const kicker = getPlayerName(play.K, players);
    const interceptor = getPlayerName(play.int, players);
    const sacker = getPlayerName(play.sack, players);
    const returner = getPlayerName(play.ret, players);
    const penaltyPlayer = getPlayerName(play.playerC, players);
    const pitchPlayer = getPlayerName(play.pitch, players);
    const passDefender = getPlayerName(play.passDef, players);
    const blockedBy = getPlayerName(play.kickBlq, players);

    const yards = play.yds || "0";
    const isLongPass = play.longPass === "1";
    const longPassText = isLongPass ? " largo" : "";
    const tackleText = getTackleText(play, players);
    const fumbleText = getFumbleText(play, players);
    const scoreText = getScoreDescription(play);

    /*
        Intercepción: tiene prioridad sobre pase normal.
    */
    if (hasValue(play.int)) {
        return `${quarterback || "El quarterback"} lanza un pase${longPassText} interceptado por ${
            interceptor || "la defensiva"
        }.${scoreText}${fumbleText}`;
    }

    /*
        Sack: no debe caer en la descripción de pase incompleto.
    */
    if (hasValue(play.sack)) {
        const assistedSack = getAssistedTackle(
            play.assSack,
            players
        );

        const secondAssistedSack = getAssistedTackle(
            play.assSackk,
            players
        );

        return `${quarterback || "El quarterback"} es capturado por ${
            sacker || "la defensiva"
        }${assistedSack}${secondAssistedSack}.${scoreText}${fumbleText}`;
    }

    /*
        Carrera.
    */
    if (play.jugada === "0") {
        let description = `${runner || "El corredor"} corre para ${yards} yardas.`;

        if (hasValue(play.pitch)) {
            if (play.pf === "1") {
                description += ` Pasa el balón a ${
                    pitchPlayer || "un compañero"
                } para ${play.ydsPitch || "0"} yardas adicionales.`;
            }

            if (play.pf === "2") {
                description += ` Pierde el balón y ${
                    pitchPlayer || "un jugador"
                } lo recupera.`;
            }
        }

        return `${description}${scoreText}${tackleText}${fumbleText}`;
    }

    /*
        Pase completo o incompleto.
    */
    if (play.jugada === "1") {
        const isComplete =
            hasValue(play.WR) &&
            hasValue(play.yds);

        if (isComplete) {
            let description = `${quarterback || "El quarterback"} pase${longPassText} con ${
                receiver || "el receptor"
            } para ${yards} yardas.`;

            if (hasValue(play.pitch)) {
                if (play.pf === "1") {
                    description += ` Pasa el balón a ${
                        pitchPlayer || "un compañero"
                    } para ${play.ydsPitch || "0"} yardas adicionales.`;
                }

                if (play.pf === "2") {
                    description += ` Pierde el balón y ${
                        pitchPlayer || "un jugador"
                    } lo recupera.`;
                }
            }

            return `${description}${scoreText}${tackleText}${fumbleText}`;
        }

        const intendedFor = receiver
            ? ` para ${receiver}`
            : "";

        const droppedPass =
            play.completo === "2" ? " El pase es soltado." : "";

        const defendedPass = passDefender
            ? ` Pase defendido por ${passDefender}.`
            : "";

        return `${quarterback || "El quarterback"} lanza un pase${longPassText} incompleto${intendedFor}.${droppedPass}${defendedPass}`;
    }

    /*
        Kickoff, punt, gol de campo, punto extra y castigos.
    */
    if (play.jugada === "2") {
        if (hasValue(play.C)) {
            const side =
                play.od === "0" ? "ofensiva" : "defensiva";

            return `Castigo de ${yards} yardas${
                penaltyPlayer
                    ? ` sobre ${penaltyPlayer}`
                    : ""
            } a la ${side}.${scoreText}`;
        }

        if (
            getNumber(play.deftd) === 2 &&
            !hasValue(play.fumbleR) &&
            !hasValue(play.int)
        ) {
            return `Safety.${scoreText}`;
        }

        if (hasValue(play.kickBlq)) {
            return `${kicker || "El pateador"} intenta una patada bloqueada por ${
                blockedBy || "la defensiva"
            }.${scoreText}${fumbleText}`;
        }

        if (getNumber(play.td) === 3) {
            return `Gol de campo de ${
                kicker || "el pateador"
            }.${scoreText}`;
        }

        if (getNumber(play.td) === 1) {
            return `${
                kicker || "el pateador"
            }.${scoreText}`;
        }

        const kickAction =
            play.d === "0" ? "realiza un kickoff" : "patea";

        let description = `${kicker || "El pateador"} ${kickAction} para ${yards} yardas.`;

        if (play.muff === "1") {
            description += " Balón muffed.";
        }

        if (hasValue(play.ret)) {
            description += ` ${
                returner || "El regresador"
            } regresa la patada para ${play.retYds || "0"} yardas.`;
        }

        if (hasValue(play.pitch)) {
            if (play.pf === "1") {
                description += ` Pasa el balón a ${
                    pitchPlayer || "un compañero"
                } para ${play.ydsPitch || "0"} yardas adicionales.`;
            }

            if (play.pf === "2") {
                description += ` Fumble recuperado por ${
                    pitchPlayer || "un jugador"
                }.`;
            }
        }

        return `${description}${scoreText}${tackleText}${fumbleText}`;
    }

    /*
        Recuperación de fumble que no entró en las reglas anteriores.
    */
    if (hasValue(play.fumbleR)) {
        return `Fumble recuperado por ${getPlayerName(
            play.fumbleR,
            players
        )}.${scoreText}`;
    }

    return "Jugada registrada sin descripción disponible.";
}
function formatDate(date?: string): string {
    const parsedDate = parseDate(date);

    if (!parsedDate) {
        return "Fecha por confirmar";
    }

    return new Intl.DateTimeFormat("es-MX", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric"
    }).format(parsedDate);
}

function getToday(): Date {
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    return today;
}

function hasNumericScore(value?: string | number): boolean {
    return (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        Number.isFinite(Number(value))
    );
}

function isCompletedGame(detail: GameDetail): boolean {
    const game = detail.Data;
    const gameDate = parseDate(game.fecha);

    if (!gameDate || gameDate >= getToday()) {
        return false;
    }

    return (
        hasNumericScore(game.resLocal) &&
        hasNumericScore(game.resVisita)
    );
}

function getGameStatus(detail: GameDetail): string {
    if (isCompletedGame(detail)) {
        return "Final";
    }

    const gameDate = parseDate(detail.Data.fecha);

    if (!gameDate) {
        return "Sin fecha";
    }

    if (gameDate < getToday()) {
        return "Pendiente";
    }

    return "Próximo";
}

function getPhaseLabel(
    phase?: string,
    week?: string | number
): string {
    if (phase === "grupos") {
        return week ? `Jornada ${week}` : "Fase de grupos";
    }

    if (phase === "1") {
        return "Final";
    }

    if (phase === "3") {
        return "Semifinal";
    }

    if (phase === "5") {
        return "Comodines";
    }

    if (phase === "7") {
        return "Play-in";
    }

    return week ? `Jornada ${week}` : "Partido";
}

function getPlayPoints(play: GamePlay): number {
    const values = [play.td, play.deftd, play.retTd];

    for (const value of values) {
        const points = Number(value);

        if (Number.isFinite(points) && points > 0) {
            return points;
        }
    }

    return 0;
}
function isTouchdownPlay(play: GamePlay): boolean {
    return (
        getNumber(play.td) === 6 ||
        getNumber(play.deftd) === 6 ||
        getNumber(play.retTd) === 6
    );
}

function getPlayScoringTeam(
    play: GamePlay,
    localId: string,
    visitorId: string
): string {
    const isDefensiveScore = getNumber(play.deftd) > 0;
    const isReturnScore = getNumber(play.retTd) > 0;

    /*
        Un touchdown defensivo o de regreso pertenece al rival
        del equipo que aparece a la ofensiva en la jugada.
    */
    if (isDefensiveScore || isReturnScore) {
        return play.ofensiva === localId
            ? visitorId
            : localId;
    }

    return play.ofensiva || "";
}


function getTeamInitial(name: string): string {
    return name.trim().charAt(0).toUpperCase() || "?";
}

const Game: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [players, setPlayers] = useState<PlayersById>({});
    const [detail, setDetail] = useState<GameDetail | null>(null);
    const [teams, setTeams] = useState<Team[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [predictionRefreshToken, setPredictionRefreshToken] =
    useState(0);

    const loadGame = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (!forceRefresh) {
                    setLoading(true);
                }

                const [gameData, teamsData, playerData] = await Promise.all([
                    getGameDetail(id, forceRefresh),
                    getTeams(forceRefresh),
                    getPlayers(forceRefresh)
                ]);

                setDetail(gameData);
                setTeams(teamsData);
                setPlayers(playerData);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar el partido:",
                    requestError
                );

                setError(
                    "No fue posible cargar la información de este partido."
                );
            } finally {
                setLoading(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadGame();
        },
        [loadGame]
    );

    const teamsById = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [String(team.id), team];
            })
        );
    }, [teams]);

    const plays = useMemo(function (): GamePlay[] {
        if (!detail?.Jugadas) {
            return [];
        }

        return Array.isArray(detail.Jugadas)
            ? detail.Jugadas
            : Object.values(detail.Jugadas);
    }, [detail]);

    const scoreEvents = useMemo(function (): ScoreEvent[] {
    if (!detail) {
        return [];
    }

    const game = detail.Data;

    return plays
        .filter(function (play) {
            return (
                getNumber(play.td) === 6 ||
                getNumber(play.deftd) === 6 ||
                getNumber(play.retTd) === 6
            );
        })
        .map(function (play, index) {
            const scoringTeamId = getPlayScoringTeam(
                play,
                game.id_local,
                game.id_visita
            );

            const scoringTeam = teamsById.get(
                String(scoringTeamId)
            );

            const teamName =
                scoringTeam?.nombre ||
                (scoringTeamId === game.id_local
                    ? game.nombre_local || "Local"
                    : scoringTeamId === game.id_visita
                    ? game.nombre_visita || "Visitante"
                    : "Equipo");

            return {
                id: `${play.q || "0"}-${index}`,
                quarter: play.q ? `${play.q}Q` : "—",
                teamId: scoringTeamId,
                teamName,
                points: getPlayPoints(play),
                description: getPlayDescription(play, players)
            };
        })
        .slice(-8)
        .reverse();
}, [detail, plays, players, teamsById]);



async function handleRefresh(
    event: CustomEvent<RefresherEventDetail>
) {
    try {
        await loadGame(true);

        setPredictionRefreshToken(function (current) {
            return current + 1;
        });
    } finally {
        event.detail.complete();
    }
}

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Partido</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="game-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !detail) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Partido</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="game-content">
                    <div className="game-empty-state">
                        <IonIcon icon={americanFootballOutline} />

                        <strong>No fue posible abrir el partido</strong>

                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadGame(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const game = detail.Data;
    const localTeam = teamsById.get(String(game.id_local));
    const visitorTeam = teamsById.get(String(game.id_visita));
    const gameFinished = isCompletedGame(detail);
    const gameStatus = getGameStatus(detail);
    const hasPlayData = plays.length > 0;

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="game-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar"
                            onClick={function () {
                                history.goBack();
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Partido</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="game-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando partido..."
                    />
                </IonRefresher>

                <section className="game-scoreboard">
                    <div className="game-scoreboard__meta">
                        <IonBadge
                            className={`game-scoreboard__status game-scoreboard__status--${gameStatus.toLocaleLowerCase(
                                "es-MX"
                            )}`}
                        >
                            {gameStatus}
                        </IonBadge>

                        <span>
                            {getPhaseLabel(
                                game.partido,
                                game.jornada
                            )}
                        </span>
                    </div>

                    <div className="game-scoreboard__date">
                        {formatDate(game.fecha)}
                    </div>

                    <div className="game-scoreboard__teams">
                        <button
                            className="game-scoreboard__team"
                            type="button"
                            onClick={function () {
                                history.push(`/team/${game.id_local}`);
                            }}
                        >
                            {localTeam?.logo ? (
                                <img
                                    src={localTeam.logo}
                                    alt={`Logo de ${
                                        localTeam.nombre ||
                                        game.nombre_local ||
                                        "equipo local"
                                    }`}
                                />
                            ) : (
                                <span className="game-scoreboard__placeholder">
                                    {getTeamInitial(
                                        localTeam?.nombre ||
                                            game.nombre_local ||
                                            "L"
                                    )}
                                </span>
                            )}

                            <strong>
                                {localTeam?.nombre ||
                                    game.nombre_local ||
                                    "Local"}
                            </strong>
                        </button>

                        <div className="game-scoreboard__result">
                            {gameFinished ? (
                                <>
                                    <strong>{game.resLocal}</strong>
                                    <span>—</span>
                                    <strong>{game.resVisita}</strong>
                                </>
                            ) : (
                                <strong>VS</strong>
                            )}
                        </div>

                        <button
                            className="game-scoreboard__team"
                            type="button"
                            onClick={function () {
                                history.push(`/team/${game.id_visita}`);
                            }}
                        >
                            {visitorTeam?.logo ? (
                                <img
                                    src={visitorTeam.logo}
                                    alt={`Logo de ${
                                        visitorTeam.nombre ||
                                        game.nombre_visita ||
                                        "equipo visitante"
                                    }`}
                                />
                            ) : (
                                <span className="game-scoreboard__placeholder">
                                    {getTeamInitial(
                                        visitorTeam?.nombre ||
                                            game.nombre_visita ||
                                            "V"
                                    )}
                                </span>
                            )}

                            <strong>
                                {visitorTeam?.nombre ||
                                    game.nombre_visita ||
                                    "Visitante"}
                            </strong>
                        </button>
                    </div>
                </section>

                <section className="game-section">
                    <div className="game-navigation">
                        <IonButton
                            expand="block"
                            fill="solid"
                            onClick={function () {
                                history.push(`/game/${id}`);
                            }}
                        >
                            Resumen
                        </IonButton>

                        <IonButton
                            expand="block"
                            fill="outline"
                            disabled={!hasPlayData}
                            onClick={function () {
                                history.push(`/gamePlays/${id}`);
                            }}
                        >
                            Jugadas
                        </IonButton>

                        <IonButton
                            expand="block"
                            fill="outline"
                            disabled={!hasPlayData}
                            onClick={function () {
                                history.push(`/gamePlayers/${id}`);
                            }}
                        >
                            Jugadores
                        </IonButton>

                        <IonButton
                            expand="block"
                            fill="outline"
                            disabled={!hasPlayData}
                            onClick={function () {
                                history.push(`/gameStats/${id}`);
                            }}
                        >
                            Estadísticas
                        </IonButton>
                    </div>
                </section>

<GamePrediction
    key={String(game.id)}
    game={game}
    refreshToken={predictionRefreshToken}
/>
                <section className="game-section">
                    <div className="game-section__heading">
                        <div>
                            <span>INFORMACIÓN</span>
                            <h2>Detalles del partido</h2>
                        </div>
                    </div>

                    <div className="game-details-card">
                        <div className="game-detail">
                            <IonIcon icon={calendarOutline} />

                            <div>
                                <span>Fecha</span>
                                <strong>{formatDate(game.fecha)}</strong>
                            </div>
                        </div>

                        <div className="game-detail">
                            <IonIcon icon={americanFootballOutline} />

                            <div>
                                <span>Fase</span>
                                <strong>
                                    {getPhaseLabel(
                                        game.partido,
                                        game.jornada
                                    )}
                                </strong>
                            </div>
                        </div>

                        <div className="game-detail">
                            <IonIcon icon={locationOutline} />

                            <div>
                                <span>Sede</span>
                                <strong>
                                    {game.sede || "Sede por confirmar"}
                                </strong>
                            </div>
                        </div>
                    </div>
                </section>

{plays.some(play => getPlayPoints(play) > 0) && (
    <ScoreBox
        game={game}
        plays={plays}
        status={gameStatus}
    />
)}

                {scoreEvents.length > 0 && (
                    <section className="game-section">
                        <div className="game-section__heading">
                            <div>
                                <span>JUGADAS DESTACADAS</span>
                                <h2>Touchdowns del partido</h2>

                            </div>

                            <IonIcon icon={statsChartOutline} />
                        </div>

                        <div className="game-scoring-list">
                            {scoreEvents.map(function (event) {
                                return (
                                    <div
                                          className="game-scoring-event"
                                          key={event.id}
                                      >
                                          <div className="game-scoring-event__meta">
                                              <span className="game-scoring-event__quarter">
                                                  {event.quarter}
                                              </span>

                                              {teamsById.get(String(event.teamId))?.logo ? (
                                                  <img
                                                      className="game-scoring-event__logo"
                                                      src={teamsById.get(String(event.teamId))?.logo}
                                                      alt={`Logo de ${event.teamName}`}
                                                  />
                                              ) : (
                                                  <span className="game-scoring-event__logo-placeholder">
                                                      {getTeamInitial(event.teamName)}
                                                  </span>
                                              )}
                                          </div>

                                          <div className="game-scoring-event__content">
                                              <div className="game-scoring-event__title">
                                                  <span>{event.teamName}</span>
                                                  <strong>TD</strong>
                                              </div>

                                              <p className="game-scoring-event__description">
                                                  {event.description}
                                              </p>
                                          </div>
                                      </div>


                                );
                            })}
                        </div>

                        <button
                            className="game-view-more"
                            type="button"
                            onClick={function () {
                                history.push(`/gamePlays/${id}`);
                            }}
                        >
                            Ver todas las jugadas
                            <IonIcon icon={chevronForwardOutline} />
                        </button>
                    </section>
                )}

<GameHighlights
    key={String(game.id)}
    detail={detail}
    players={players}
    refreshToken={predictionRefreshToken}
/>

<GameStandings
    gameId={String(game.id)}
    competitionId={String(game.id_comp)}
    gameDate={game.fecha}
    localTeamId={String(game.id_local)}
    visitorTeamId={String(game.id_visita)}
    teams={teams}
    refreshToken={predictionRefreshToken}
/>
            </IonContent>
        </IonPage>
    );
};

export default Game;
